import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Check, CreditCard, Loader2, MessageCircle, Search } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FILTERS, PRODUCTS } from "@/data/products";
import { imageUrl, waLink } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Catalog() {
  const [products, setProducts] = useState(PRODUCTS);
  const [filter, setFilter] = useState("todos");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [buying, setBuying] = useState(null);

  const handleBuy = async (e, p) => {
    e.stopPropagation();
    setBuying(p.id);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: p.id,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Compra online indisponível no momento. Peça pelo WhatsApp!"
      );
      setBuying(null);
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) setProducts(res.data);
      })
      .catch(() => {});
  }, []);

  const visible = useMemo(
    () =>
      products.filter(
        (p) =>
          (filter === "todos" || p.category === filter) &&
          p.name.toLowerCase().includes(query.trim().toLowerCase())
      ),
    [products, filter, query]
  );

  return (
    <section id="catalogo" data-testid="catalog-section" className="bg-white/50 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-12 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between"
        >
          <div className="max-w-2xl">
            <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-clay">
              Seleção Carmo
            </p>
            <h2 className="font-display text-3xl font-medium tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Destaques da prateleira
            </h2>
            <p className="mt-4 text-base text-moss sm:text-lg">
              Produtos testados e aprovados por quem cria, planta e ama. Peça pelo WhatsApp e
              receba em casa.
            </p>
          </div>

          <div className="relative w-full max-w-sm">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-moss" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar produto..."
              data-testid="catalog-search-input"
              className="w-full rounded-full border border-linen bg-parchment py-3 pl-11 pr-4 text-sm text-ink outline-none transition-colors placeholder:text-moss/60 focus:border-clay"
            />
          </div>
        </motion.div>

        <div className="mb-10 flex flex-wrap gap-2" role="tablist" aria-label="Filtrar produtos">
          {FILTERS.map((f) => (
            <button
              key={f.slug}
              type="button"
              role="tab"
              aria-selected={filter === f.slug}
              data-testid={`filter-tab-${f.slug}`}
              onClick={() => setFilter(f.slug)}
              className={`relative rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                filter === f.slug ? "text-parchment" : "text-olive hover:bg-linen/60"
              }`}
            >
              {filter === f.slug && (
                <motion.span
                  layoutId="filter-pill"
                  className="absolute inset-0 rounded-full bg-olive"
                  transition={{ type: "spring", stiffness: 400, damping: 32 }}
                />
              )}
              <span className="relative z-10">{f.label}</span>
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.35 }}
                data-testid={`product-card-${p.id}`}
                onClick={() => setSelected(p)}
                className="group cursor-pointer overflow-hidden rounded-[1.75rem] border border-linen bg-white shadow-sm transition-shadow duration-300 hover:shadow-xl hover:shadow-olive/10"
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={imageUrl(p.image)}
                    alt={p.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  {p.badge && (
                    <span className="absolute left-4 top-4 rounded-full bg-clay px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-parchment">
                      {p.badge}
                    </span>
                  )}
                  <span className="absolute right-4 top-4 rounded-full bg-forest/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-parchment backdrop-blur">
                    {p.categoryLabel}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg font-medium leading-snug text-ink">{p.name}</h3>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="font-display text-xl font-semibold text-olive">{p.price}</span>
                    <span className="text-xs text-moss">{p.unit}</span>
                  </div>
                  <div className="mt-4 space-y-2">
                    {p.price !== "Consulte" && (
                      <button
                        type="button"
                        onClick={(e) => handleBuy(e, p)}
                        disabled={buying === p.id}
                        data-testid={`product-buy-${p.id}`}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-clay px-4 py-2.5 text-sm font-semibold text-parchment transition-colors duration-300 hover:bg-olive disabled:opacity-60"
                      >
                        {buying === p.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <CreditCard className="h-4 w-4" />
                        )}
                        Comprar online
                      </button>
                    )}
                    <a
                      href={waLink(`Olá, Agro Carmo! Quero pedir: ${p.name} (${p.price} — ${p.unit}).`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`product-whatsapp-${p.id}`}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-olive px-4 py-2.5 text-sm font-semibold text-olive transition-colors duration-300 hover:bg-olive hover:text-parchment"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Pedir no WhatsApp
                    </a>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        {visible.length === 0 && (
          <p data-testid="catalog-empty-message" className="py-16 text-center text-moss">
            Nenhum produto encontrado. Chame a gente no WhatsApp que a gente procura para você!
          </p>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent
          data-testid="product-modal"
          className="max-w-2xl overflow-hidden rounded-[2rem] border-linen bg-parchment p-0"
        >
          {selected && (
            <div className="grid sm:grid-cols-2">
              <div className="relative h-56 sm:h-full">
                <img src={imageUrl(selected.image)} alt={selected.name} className="absolute inset-0 h-full w-full object-cover" />
              </div>
              <div className="p-7">
                <DialogHeader>
                  <span className="mb-2 inline-block w-fit rounded-full bg-linen/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-olive">
                    {selected.categoryLabel}
                  </span>
                  <DialogTitle className="font-display text-2xl font-medium text-ink">
                    {selected.name}
                  </DialogTitle>
                  <DialogDescription className="text-sm leading-relaxed text-moss">
                    {selected.short}
                  </DialogDescription>
                </DialogHeader>
                <ul className="mt-5 space-y-2">
                  {(selected.specs || []).map((spec) => (
                    <li key={spec} className="flex items-start gap-2 text-sm text-ink/80">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-clay" />
                      {spec}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-display text-3xl font-semibold text-olive">{selected.price}</span>
                  <span className="text-sm text-moss">{selected.unit}</span>
                </div>
                <div className="mt-6 space-y-3">
                  {selected.price !== "Consulte" && (
                    <button
                      type="button"
                      onClick={(e) => handleBuy(e, selected)}
                      disabled={buying === selected.id}
                      data-testid="modal-buy-button"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-clay px-5 py-3.5 text-sm font-semibold text-parchment transition-colors duration-300 hover:bg-olive disabled:opacity-60"
                    >
                      {buying === selected.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CreditCard className="h-4 w-4" />
                      )}
                      Comprar online agora
                    </button>
                  )}
                  <a
                    href={waLink(`Olá, Agro Carmo! Quero pedir: ${selected.name} (${selected.price} — ${selected.unit}).`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="modal-whatsapp-button"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-olive px-5 py-3.5 text-sm font-semibold text-olive transition-colors duration-300 hover:bg-olive hover:text-parchment"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Pedir no WhatsApp
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
